import { amount, type UsageReport } from "@/lib/api/billing";
import type { CatalogueStatus } from "@/lib/api/catalogue";
import type { OverviewDay, OverviewStatuses, QueueEntry } from "@/lib/api/envoy";
import { countWord } from "@/lib/greeting";
import { formatCount } from "@/lib/format";
import { dayOf } from "@/lib/posting/dates";

/**
 * The Overview, as data: every figure, line and bar the page draws,
 * computed from what envoy, the catalogue and billing answer. Pure, so
 * the page is a function of these and the tests read the words.
 */

const TAILORS = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2, useGrouping: false });

/** A tailor count as billing keeps it: whole, or with its fraction ("7.5"). */
export function formatTailors(value: number): string {
    // Never "NaN", "-0" or "1e+21": a count that is not one is none.
    if (!Number.isFinite(value)) return "0";
    const rounded = Math.round(value * 100) / 100;
    return TAILORS.format(rounded === 0 ? 0 : rounded);
}

/** What waits on the person: resumes ready to send, and matches that need a hand. */
export interface Waiting {
    total: number;
    ready: number;
    needsYou: number;
    /** "4 ready to send, 2 need a hand". */
    line: string;
}

export function waitingOn(statuses: OverviewStatuses): Waiting {
    const ready = statuses.ready;
    // A failed tailor waits on the person too: they can still send it with
    // their saved resume, or decline it.
    const needsYou = statuses.needs_you + statuses.failed;
    const total = ready + needsYou;
    const parts = [
        ready > 0 ? `${formatCount(ready)} ready to send` : null,
        needsYou > 0
            ? `${formatCount(needsYou)} ${needsYou === 1 ? "needs" : "need"} a hand`
            : null,
    ].filter((part): part is string => part !== null);
    return { total, ready, needsYou, line: parts.length > 0 ? parts.join(", ") : "Nothing yet" };
}

/** Every posting the envoy ever matched to the person, wherever it stands now. */
export function everMatched(statuses: OverviewStatuses): number {
    return (
        statuses.matched +
        statuses.tailoring +
        statuses.ready +
        statuses.needs_you +
        statuses.applied +
        statuses.failed +
        statuses.skipped +
        statuses.closed
    );
}

export type PipelineKey = "applied" | "queue" | "done" | "open";

export interface PipelineSegment {
    key: PipelineKey;
    label: string;
    count: number;
    /** Its share of the bar, from 0 to 1. */
    share: number;
}

/**
 * Where every match stands, in four parts: applied by the person; in the
 * queue (queued, tailoring, ready, needing them); skipped or closed;
 * and open matches nobody has added. A match waiting in the
 * queue is told from one not added by the queue's own entries.
 */
export function pipeline(
    statuses: OverviewStatuses,
    queue: readonly QueueEntry[],
): PipelineSegment[] {
    const queuedWaiting = queue.filter((entry) => entry.application.status === "MATCHED").length;
    const counts: Record<PipelineKey, number> = {
        applied: statuses.applied,
        queue:
            Math.min(queuedWaiting, statuses.matched) +
            statuses.tailoring +
            statuses.ready +
            statuses.needs_you +
            statuses.failed,
        done: statuses.skipped + statuses.closed,
        open: Math.max(0, statuses.matched - queuedWaiting),
    };
    const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
    const labels: Record<PipelineKey, string> = {
        applied: "Applied, by you",
        queue: "In the queue: ready, needs you, processing",
        done: "Skipped, or closed",
        open: "Open, not yet added",
    };
    return (["applied", "queue", "done", "open"] as const).map((key) => ({
        key,
        label: labels[key],
        count: counts[key],
        share: total === 0 ? 0 : counts[key] / total,
    }));
}

/** The tailors meter: what is left of the period and the packs, and of how many. */
export interface TailorsLeft {
    left: number;
    limit: number;
    packs: number;
    /** When the period's allowance resets. */
    resetsAt: string;
}

export function tailorsLeft(usage: UsageReport): TailorsLeft | null {
    const meter = usage.meters.find((entry) => entry.meter === "tailors");
    if (!meter) return null;
    const packs = amount(meter.credits_remaining);
    return {
        left: amount(meter.remaining) + packs,
        limit: amount(meter.limit),
        packs,
        resetsAt: usage.period_end,
    };
}

/** "13 Nov", and "13 Sep" where an engine would say "Sept". A date that does not read, as written. */
export function shortDate(value: string, timeZone?: string): string {
    return dayOf(value, timeZone);
}

/** "Monday, 12 October", for the line over the greeting. */
export function longDate(now: Date, timeZone?: string): string {
    if (Number.isNaN(now.getTime())) return "";
    const parts = new Intl.DateTimeFormat("en-GB", {
        weekday: "long",
        day: "numeric",
        month: "long",
        timeZone,
    }).formatToParts(now);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
        parts.find((entry) => entry.type === type)?.value ?? "";
    return `${part("weekday")}, ${part("day")} ${part("month")}`;
}

/** The sentence under the greeting: what waits, and how many postings are new. */
export function summarySentence(waiting: Waiting, status: CatalogueStatus | undefined): string {
    const first =
        waiting.total === 0
            ? "Nothing is waiting on you."
            : `${countWord(waiting.total)} ${waiting.total === 1 ? "application is" : "applications are"} waiting on you${
                  waiting.ready > 0 && waiting.needsYou > 0
                      ? `: ${countWord(waiting.ready).toLowerCase()} ready to send, ${countWord(waiting.needsYou).toLowerCase()} that ${waiting.needsYou === 1 ? "needs" : "need"} a hand`
                      : waiting.ready > 0
                        ? ", ready to send"
                        : ", each needing a hand"
              }.`;
    if (!status) {
        return first;
    }
    const found = status.found_last_day;
    return `${first} ${
        found === 0
            ? "Nothing new"
            : `${formatCount(found)} new ${found === 1 ? "posting" : "postings"}`
    } in the last day.`;
}

/** One day of the past two weeks' chart, its two bars in pixels and its label. */
export interface ChartDay {
    date: string;
    label: string;
    matched: number;
    applied: number;
    matchedHeight: number;
    appliedHeight: number;
}

const DAY_LABEL = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
});

/** The tallest bar's height, and the stub a zero still shows. */
export const CHART_HEIGHT = 154;
const STUB = 2;

/**
 * The past two weeks as bars scaled to the busiest day, a label on every
 * other day starting with the first ("Sep 29", "", "Oct 1"…).
 */
export function chartDays(days: readonly OverviewDay[]): ChartDay[] {
    const label = (date: string) => {
        const day = new Date(`${date}T00:00:00Z`);
        return Number.isNaN(day.getTime()) ? "" : DAY_LABEL.format(day);
    };
    const top = Math.max(1, ...days.map((day) => Math.max(day.matched, day.applied)));
    const scale = (value: number) =>
        value === 0 ? STUB : Math.max(STUB, Math.round((value / top) * CHART_HEIGHT));
    return days.map((day, index) => ({
        date: day.date,
        label: index % 2 === 0 ? label(day.date) : "",
        matched: day.matched,
        applied: day.applied,
        matchedHeight: scale(day.matched),
        appliedHeight: scale(day.applied),
    }));
}

/**
 * A tile's sparkline: the series as an SVG polyline in a 72 by 26 box,
 * 2 px of headroom top and bottom. A flat or empty series draws a level
 * line through the middle.
 */
export function sparkline(series: readonly number[], width = 72, height = 26): string {
    // A value that is not a number draws as nothing, not as a hole in the line.
    const values = series.map((value) => (Number.isFinite(value) ? value : 0));
    if (values.length === 0) return `0,${height / 2} ${width},${height / 2}`;
    if (values.length === 1) return `0,${height / 2} ${width},${height / 2}`;
    const top = Math.max(...values);
    const bottom = Math.min(...values);
    const span = top - bottom;
    const step = width / (values.length - 1);
    return values
        .map((value, index) => {
            const across = Math.round(index * step * 10) / 10;
            const down = span === 0 ? height / 2 : 2 + ((top - value) / span) * (height - 4);
            return `${across},${Math.round(down * 10) / 10}`;
        })
        .join(" ");
}

/** "10:40", a time in the person's clock. */
export function clockTime(value: string, timeZone?: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone,
    }).format(date);
}
